import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AnalyticsService } from 'smartauction-frontend-micro-library';
import {
  AuctionFeeSchedule,
  AuthService,
  BuyerAuctionFee,
  Configuration,
  ConfigurationState,
  FinanceSource,
  FinanceSourceService,
  LaunchDarklyService,
  Organization,
  OrganizationService,
  DateUtilsService,
  AuctionFeeService
} from 'remarketing-angular-library';
import { map, take, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { tenantClass } from '../../util/state-selectors';
import { Router } from '@angular/router';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { ServiceFeeScheduleModalComponent } from 'src/shared/ui/modals/service-fee-schedule-modal/service-fee-schedule-modal.component';
import { HttpParams } from '@angular/common/http';
import { LegacyTableModule } from 'remarketing-ui-components-lib';
import { SuccessMessageComponent } from '../success-message/success-message.component';
import { ErrorMessageComponent } from '../error-message/error-message.component';
import { AsyncPipe, CurrencyPipe } from '@angular/common';

@Component({
    selector: 'app-dealer-credit',
    templateUrl: './dealer-credit.component.html',
    styleUrls: ['./dealer-credit.component.scss'],
    imports: [
        TranslocoDirective,
        ErrorMessageComponent,
        SuccessMessageComponent,
        LegacyTableModule,
        AsyncPipe,
        CurrencyPipe,
        ServiceFeeScheduleModalComponent
    ]
})
export class DealerCreditComponent implements OnInit, OnDestroy {
  @ViewChild(ServiceFeeScheduleModalComponent) serviceFeeScheduleModal: ServiceFeeScheduleModalComponent;
  @Select(ConfigurationState.configuration) private configuration$: Observable<Configuration>;
  @Select(tenantClass) tenantClass$: Observable<string>;
  tenantName$: Observable<string>;

  private destroyed$ = new Subject();

  financeSources: FinanceSource[] = [];

  auctionFeeSources: BuyerAuctionFee[] = [];

  auctionFeeSchedule = {} as AuctionFeeSchedule;

  defaultSource: FinanceSource;

  organization: Organization;

  creditColumns: any = [
    { key: 'financeSource', label: '' },
    { key: 'creditLimit', label: '' },
    { key: 'expDate', label: '' },
    { key: 'options', label: '' }
  ];

  auctionColumns: any = [
    { key: 'auctionLane', label: '' },
    { key: 'serviceFee', label: '' }
  ];

  showError = false;
  showSuccess = false;
  message: string;
  textMessage: string;

  @ViewChild('pageHeading') pageHeading: ElementRef;
  routerState: any;

  private authService: AuthService;

  constructor(
    private organizationService: OrganizationService,
    private store: Store,
    private readonly financeSourceService: FinanceSourceService,
    private readonly auctionFeeService: AuctionFeeService,
    private analyticsService: AnalyticsService,
    private readonly router: Router,
    private readonly translocoService: TranslocoService,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly dateUtilsService: DateUtilsService
  ) {
    this.authService = AuthService.getInstance();
    const currentNaviation = this.router.currentNavigation();

    if (currentNaviation && currentNaviation.extras && currentNaviation.extras.state) {
      this.routerState = currentNaviation.extras.state;
    }
  }

  get feeScheduleModalId() {
    return 'Fee-Schedule';
  }

  ngOnInit() {
    this.getTranslations();
    this.tenantName$ = this.configuration$.pipe(map((configuration: Configuration) => configuration.tenantName));

    const params = new HttpParams().append('expandFinanceSources', false);
    this.financeSourceService
      .getFinanceSourceList(params)
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe(res => {
        this.financeSources = res.data;
        const defaultFinanceSource = res.data.find(source => source.defaultIndicator);
        if (defaultFinanceSource) {
          this.setDefault(defaultFinanceSource.financeSourceUuid, defaultFinanceSource.achSelected);
        }
      });

    if (this.isBuyerAuctionFeesEnabled) {
      this.getAuctionFeeSources();
    }

    this.organization = this.store.selectSnapshot(_state => _state.org.organization);

    this.analyticsService.trackPageLoad({
      page: {
        pageName: `${this.analyticsService.digitalData.page.category.site}:Profile Settings:Dealership:Buyer`,
        section: 'Profile Settings',
        subsection: 'Dealership',
        title: 'Buyer'
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.routerState && this.routerState.focus) {
      this.pageHeading.nativeElement.focus();
    }
  }

  localizeDate(date) {
    return this.dateUtilsService.localizeDate(date);
  }

  effectiveCreditLimit(row: FinanceSource) {
    const expDate = row.temporaryCreditLimitExpirationDate;
    const hasTempLimit = row.temporaryDailyCreditLimit != null; // do not simply check if it is truthy; 0 is a valid credit limit
    return hasTempLimit && expDate && !this.dateUtilsService.isLessThanCurrentDateAndTime(expDate)
      ? row.temporaryDailyCreditLimit
      : row.dailyCreditLimit;
  }

  updateDefault(uuid: string, isAch: boolean) {
    this.showError = false;
    this.showSuccess = false;
    const payload = {
      buyerRestrictionDefault: true,
      financeSourceUuid: uuid,
      financeSourceAch: isAch
    };

    this.financeSourceService
      .updateDefaultFinanceSource(this.organization.oeUuid, payload)
      .pipe(take(1))
      .subscribe(
        () => {
          this.setDefault(uuid, isAch);

          this.translocoService
            .selectTranslate('profileManagement.dealercredit.textSuccesfullySet', {
              financeSourceName: this.defaultSource.financeSourceName
            })
            .pipe(take(1))
            .subscribe(translate => {
              this.textMessage = translate;
            });
          // set success message
          this.showSuccess = true;
          this.message = this.textMessage;
        },
        error => {
          this.showError = true;
        }
      );
  }

  setDefault(uuid: string, isAch: boolean) {
    this.financeSources.sort((a, b) => {
      return a.financeSourceName.localeCompare(b.financeSourceName);
    });

    // find default source and index
    this.defaultSource = this.financeSources.find(
      source => source.financeSourceUuid === uuid && source.achSelected === isAch
    );
    const defaultIndex = this.financeSources.findIndex(
      source => source.financeSourceUuid === uuid && source.achSelected === isAch
    );

    // move default source to beginning of array
    this.financeSources.splice(defaultIndex, 1);
    this.financeSources.unshift(this.defaultSource);
  }

  isDefault(financeSource: FinanceSource) {
    if (this.defaultSource) {
      return (
        financeSource &&
        financeSource.financeSourceUuid === this.defaultSource.financeSourceUuid &&
        financeSource.achSelected === this.defaultSource.achSelected
      );
    } else {
      return false;
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getTranslations() {
    this.translocoService
      .selectTranslateObject('profileManagement.dealercredit.tableHeaderCreditLines')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.creditColumns[0].label = translate.labelFinanceSource;
        this.creditColumns[1].label = translate.labelCredit;
        this.creditColumns[2].label = translate.labelExpiration;
        this.creditColumns[3].label = translate.labelOption;
      });
    this.translocoService
      .selectTranslateObject('profileManagement.dealercredit.tableHeaderAuctionFees')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.auctionColumns[0].label = translate.labelAuctionLane;
        this.auctionColumns[1].label = translate.labelServiceFee;
      });
  }

  viewScheduleFees(auctionFee: BuyerAuctionFee) {
    this.auctionFeeSchedule.auctionFeeScheduleRanges = auctionFee.auctionFeeScheduleRanges;
    this.serviceFeeScheduleModal.auctionFeeSchedule = this.auctionFeeSchedule;
    this.serviceFeeScheduleModal.show();
  }

  sortByAuctionName() {
    return this.auctionFeeSources.sort((a, b) => (a.auctionName < b.auctionName ? -1 : 1));
  }

  get isBuyerAuctionFeesEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('profile-dealershipBuyerAuctionFeesEnabled', false);
  }

  getAuctionFeeSources() {
    this.auctionFeeService
      .getAuctionFeeSchedules()
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe(res => {
        this.auctionFeeSources = res;
        this.sortByAuctionName();
      });
  }
}
